import { Icons } from '@/components/icons';
import PriceDisplay from '@/components/PriceDisplay';
import { Button } from '@/components/primitives/Button';
import {
  PopoverContent,
  PopoverRoot,
  PopoverTrigger,
} from '@/components/primitives/Popover';
import { Typography } from '@/components/primitives/Typography';
import { PassengerSelectionItem } from '@/components/purchase/checkout/seat-selection/modal/PassengerSelectionItem';
import { getSeatSelectionBookingById } from '@/features/purchase/purchaseActions';
import { purchaseFlowBookingSelector } from '@/features/purchase/purchaseSelectors';
import {
  fetchPlaceAvailability,
  undoSeatSelection,
} from '@/features/purchase/purchaseService';
import { useSeatSelection } from '@/hooks/useSeatSelection';
import { useSeatSelectionLegs } from '@/hooks/useSeatSelectionLegs';
import { TransText } from '@/i18n/trans/text';
import { useDispatch, useSelector } from '@/store/utils';
import type { PlaceDTO } from '@/types/dto/agent';
import {
  findBookingBookedTripFeesByFeeRefs,
  findBookingBookedTripReservationByReservationId,
} from '@/utils/booking';
import { breakpoints } from '@/utils/breakpoints';
import { cn } from '@/utils/cn';
import { getPassengerName } from '@/utils/passenger';
import { type FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useMediaQuery } from 'react-responsive';

type NotificationType = 'success' | 'error' | null;

interface SeatSelectionFooterProps {}

export const SeatSelectionFooter: FC<SeatSelectionFooterProps> = () => {
  const dispatch = useDispatch();
  const booking = useSelector(purchaseFlowBookingSelector);
  const {
    selectedLegId,
    selectedReservationId,
    closeModal,
    handleReservationSelect,
    isLoading,
    seatSelectionSuccess,
    seatSelectionError,
    refreshPlaceAvailability,
    setSelectedFloorIndex,
    setSelectedCarriageIndex,
    isUndoing,
    setIsUndoing,
  } = useSeatSelection();
  const [showNotification, setShowNotification] = useState(false);
  const [notificationType, setNotificationType] =
    useState<NotificationType>(null);
  const { legs } = useSeatSelectionLegs();
  const isLaptopOrBigger = useMediaQuery({
    minWidth: breakpoints.laptop,
  });

  const selectedLeg = useMemo(
    () => legs.find((leg) => leg.id === selectedLegId),
    [legs, selectedLegId]
  );

  const selectedPassengerData = useMemo(() => {
    if (!selectedLeg) return null;

    const passengerIndex = selectedLeg.passengers.findIndex(
      (p) => p.reservationId === selectedReservationId
    );

    return {
      passenger: selectedLeg.passengers[passengerIndex],
      index: passengerIndex,
    };
  }, [selectedLeg, selectedReservationId]);
  const IconComponent =
    Icons[selectedPassengerData?.passenger.placeIcon ?? 'seat'];

  const reservation = useMemo(
    () =>
      findBookingBookedTripReservationByReservationId(
        booking,
        selectedReservationId
      ),
    [booking, selectedReservationId]
  );

  const seatChangeFees = useMemo(
    () => findBookingBookedTripFeesByFeeRefs(booking, reservation?.feeRefs),
    [booking, reservation?.feeRefs]
  );

  const reservedPlace = useMemo(
    () => reservation?.placeAllocations?.reservedPlaces?.[0],
    [reservation]
  );

  const handleUndo = useCallback(async () => {
    if (!booking?.id || !selectedReservationId) return;

    const reservation = findBookingBookedTripReservationByReservationId(
      booking,
      selectedReservationId
    );
    if (!reservation?.feeRefs?.[0]) return;

    setShowNotification(false);
    setIsUndoing(true);
    try {
      await undoSeatSelection(booking.id, reservation.feeRefs[0]);

      const updatedBooking = await dispatch(
        getSeatSelectionBookingById(booking.id)
      ).unwrap();
      const newPlaceMap = await fetchPlaceAvailability(
        booking.id,
        selectedReservationId
      );

      await refreshPlaceAvailability();

      const updatedReservation =
        findBookingBookedTripReservationByReservationId(
          updatedBooking,
          selectedReservationId
        );
      const reservedSeat =
        updatedReservation?.placeAllocations?.reservedPlaces?.[0];

      if (reservedSeat && newPlaceMap?.placeMaps?.length) {
        const coachIndex = newPlaceMap.placeMaps.findIndex(
          (coach) => coach.number === reservedSeat.coachNumber
        );

        setSelectedCarriageIndex(coachIndex >= 0 ? coachIndex : 0);

        if (coachIndex >= 0) {
          const coach = newPlaceMap.placeMaps[coachIndex];
          if (coach?.floors?.length) {
            for (let i = 0; i < coach.floors.length; i++) {
              const floor = coach.floors[i];
              const isOnThisFloor = floor.places?.some(
                (place: PlaceDTO) =>
                  place.number === reservedSeat.placeNumber?.[0]
              );

              if (isOnThisFloor) {
                setSelectedFloorIndex(i);
                break;
              }
            }
          }
        }
      }
    } finally {
      setIsUndoing(false);
    }
  }, [
    booking,
    selectedReservationId,
    setIsUndoing,
    dispatch,
    refreshPlaceAvailability,
    setSelectedCarriageIndex,
    setSelectedFloorIndex,
  ]);

  useEffect(() => {
    if (!seatSelectionSuccess && !seatSelectionError) return;

    const type = seatSelectionSuccess ? 'success' : 'error';

    setNotificationType(type);
    setShowNotification(true);

    const timer = setTimeout(() => {
      setShowNotification(false);
      setNotificationType(null);
    }, 5000);

    return () => clearTimeout(timer);
  }, [seatSelectionSuccess, seatSelectionError]);

  return (
    <div className="relative flex w-full flex-col justify-between gap-2 laptop:flex-row">
      {showNotification && (
        <div
          className={cn(
            'absolute -top-16 left-3 flex items-center gap-2 rounded-md border px-3 py-2',
            {
              'border-success-text bg-success-surface':
                notificationType === 'success',
              'border-neutral bg-neutral-pale': notificationType === 'error',
            }
          )}
          role="alert"
        >
          {notificationType === 'success' ? (
            <>
              <Icons.checkCircle className="h-6 w-6 text-success-text" />
              <Typography variant="paragraph" className="text-dark">
                <TransText i18nKey="seatSelectionSuccess" />
              </Typography>
              {!!seatChangeFees?.length && (
                <Button
                  variant="ghost"
                  size="small"
                  disabled={isUndoing}
                  onClick={handleUndo}
                  className="ml-4 p-0"
                  data-testid="undo-seat-change-button"
                >
                  <Icons.history className="h-4 w-4" />
                  <TransText i18nKey="undo" />
                </Button>
              )}
            </>
          ) : (
            <>
              <Icons.infoCircle className="h-6 w-6 text-dark" />
              <Typography variant="paragraph" className="text-dark">
                <TransText i18nKey="seatSelectionError" />
              </Typography>
            </>
          )}
        </div>
      )}

      <div className="flex flex-row-reverse items-center justify-between gap-4 laptop:flex-row laptop:justify-normal">
        <PopoverRoot>
          <PopoverTrigger asChild>
            <Button
              data-testid="passenger-select-trigger"
              variant="ghostLink"
              size="small"
              className="group text-primary"
              disabled={isLoading}
            >
              <TransText i18nKey="passengers" />
              <Icons.chevronDown
                height={16}
                width={16}
                className="transition-transform duration-200 group-data-[state=open]:rotate-180"
              />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className={cn('min-w-[360px] border-neutral-light p-4', {
              'w-screen rounded-none border-l-0 border-r-0 border-t-0 border-b-primary bg-white shadow-[0_-6px_4px_-1px_rgba(0,0,0,0.05),0_-1px_2px_-1px_rgba(0,0,0,0.05)]':
                !isLaptopOrBigger,
            })}
            align="start"
            side="top"
            sideOffset={isLaptopOrBigger ? 28 : 13}
          >
            <div className="flex flex-col gap-2">
              {selectedLeg?.passengers.map((passenger, index) => (
                <PassengerSelectionItem
                  key={passenger.reservationId}
                  passenger={passenger}
                  isSelected={passenger.reservationId === selectedReservationId}
                  onSelect={() => {
                    if (passenger.reservationId) {
                      handleReservationSelect(passenger.reservationId);
                    }
                  }}
                  passengerIndex={index}
                  isDisabled={isLoading}
                />
              ))}
            </div>
          </PopoverContent>
        </PopoverRoot>

        <div className="flex flex-col">
          {selectedPassengerData?.passenger && (
            <>
              <div className="flex items-center gap-2">
                <Typography variant="body2-bold" className="text-primary">
                  {getPassengerName(
                    selectedPassengerData.passenger,
                    selectedPassengerData.index
                  )}
                </Typography>

                {selectedLeg?.ptMode?.code !== 'BUS' && (
                  <Typography
                    variant="body2-bold"
                    className="flex items-center gap-1 text-primary"
                  >
                    <Icons.carriage
                      width={12}
                      height={12}
                      className="text-primary"
                    />
                    {reservedPlace?.coachNumber}
                  </Typography>
                )}

                <Typography
                  variant="body2-bold"
                  className="flex items-center gap-1 text-primary"
                >
                  <IconComponent height={12} width={12} />
                  {reservedPlace?.placeNumber?.join(', ')}
                </Typography>
              </div>

              {!!seatChangeFees?.length && (
                <div className="flex items-center gap-2">
                  <Typography variant="body2" className="text-neutral">
                    <TransText i18nKey="changedSeat" />:
                  </Typography>
                  <Typography variant="body2-bold" className="text-primary">
                    <PriceDisplay
                      amount={seatChangeFees.reduce(
                        (acc, cur) => acc + (cur.price?.amount || 0),
                        0
                      )}
                    />
                  </Typography>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <Button
        onClick={closeModal}
        variant="primary"
        data-testid="close-seat-selection"
        disabled={isLoading || isUndoing}
        size={isLaptopOrBigger ? 'regular' : 'large'}
        fullWidth
        className="rounded-lg laptop:w-auto"
      >
        <Icons.check
          width={isLaptopOrBigger ? 16 : 24}
          height={isLaptopOrBigger ? 16 : 24}
        />
        <TransText i18nKey="done" />
      </Button>
    </div>
  );
};
